import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { needsYou } from '@/components/hiring/candidateList';
import { formatShortDate } from '@/components/hiring/reviewerStages';
import { candidacyService } from '@/shared/services/candidacyService';
import { positionService } from '@/shared/services/positionService';
import { positionPath, sectionLabel } from './positionLinks';
import './FacultyHiringCard.css';

const PROGRAM = 'la';
export const EMPTY_TEXT = 'No students have applied to your sections yet.';
const NOT_SHARED_TEXT = 'The program reviews the applicants for this section.';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function placesLine(position, review) {
    if (position.status === 'pending') return 'Award not final yet';
    return [
        `${plural(position.openSlots || 0, 'place', 'places')} open of ${position.awarded || 0}`,
        `${review} to review`,
    ].join(' \u00b7 ');
}

// The one thing to do first: an expired offer holds a place, then offers
// waiting on a student. Review counts are on the line above.
function nextIssue(position, candidacies, needs) {
    const expired = candidacies.find((c) => c.stage === 'expired');
    if (expired) {
        return (
            <>
                <Link to={positionPath(position.applicationId, expired.id)}>
                    {expired.student?.name || 'A student'}
                </Link>
                {"'s offer expired: offer again or release the place."}
            </>
        );
    }
    if (needs.offers > 0) {
        return `${plural(needs.offers, 'offer is', 'offers are')} awaiting an answer${
            needs.firstDue
                ? `, the first due ${formatShortDate(needs.firstDue)}`
                : ''
        }.`;
    }
    return null;
}

function SectionBlock({ position, result }) {
    const label = sectionLabel(position);
    const headingId = `hiring-card-${position.applicationId}`;
    let content;
    if (result.status === 'forbidden') {
        content = <p className="mb-0 small">{NOT_SHARED_TEXT}</p>;
    } else if (result.status === 'error') {
        content = (
            <p className="mb-0 small">
                The students for this section couldn&apos;t be loaded.
            </p>
        );
    } else {
        const needs = needsYou(result.candidacies, null);
        const issue = nextIssue(position, result.candidacies, needs);
        content = (
            <>
                <p className="mb-1 small">
                    {placesLine(position, needs.review)}
                </p>
                <p className="mb-2 small text-body-secondary">
                    {result.candidacies.length === 0
                        ? 'No students have applied yet.'
                        : issue || 'No offers are waiting on an answer.'}
                </p>
                <Link
                    to={positionPath(position.applicationId)}
                    className={`btn hiring-card-action ${needs.review > 0 ? 'btn-primary' : 'btn-outline-primary'}`}
                    aria-label={`Review candidates for ${label}`}
                >
                    Review candidates
                </Link>
            </>
        );
    }
    return (
        <section aria-labelledby={headingId} className="mb-4">
            <h3 id={headingId} className="h6 mb-1">
                <Link to={positionPath(position.applicationId)}>{label}</Link>
                <span className="fw-normal text-body-secondary">
                    {` ${position.title}`}
                </span>
            </h3>
            {content}
        </section>
    );
}

/**
 * The faculty dashboard's hiring card: each LA section the faculty member
 * holds, its open places, how many students await review and the next
 * issue, with a link to review them. The roster stays on My LA positions.
 * It reads the same lists that page reads, so it shows nothing that page
 * would not.
 */
export default function FacultyHiringCard({ term }) {
    const [state, setState] = useState({ status: 'loading' });
    const requestRef = useRef(0);

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        setState({ status: 'loading' });
        try {
            const answer = await positionService.listPositions({
                program: PROGRAM,
                term,
            });
            const positions = answer.positions || [];
            const lists = await Promise.allSettled(
                positions.map((p) =>
                    candidacyService.listForPosition(p.applicationId, PROGRAM)
                )
            );
            if (request !== requestRef.current) return;
            const sections = positions.map((position, index) => {
                const list = lists[index];
                if (list.status === 'fulfilled') {
                    return {
                        position,
                        result: {
                            status: 'ok',
                            candidacies: list.value?.candidacies || [],
                        },
                    };
                }
                return {
                    position,
                    result: {
                        status:
                            list.reason?.response?.status === 403
                                ? 'forbidden'
                                : 'error',
                    },
                };
            });
            setState({ status: 'ok', sections });
        } catch (_err) {
            if (request === requestRef.current) setState({ status: 'error' });
        }
    }, [term]);

    useEffect(() => {
        if (!term) return undefined;
        load();
        return () => {
            requestRef.current += 1;
        };
    }, [term, load]);

    let body;
    if (state.status === 'loading') {
        body = (
            <div role="status" className="py-2">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading your applicants...
            </div>
        );
    } else if (state.status === 'error') {
        body = (
            <Alert variant="danger" className="mb-0">
                <p className="mb-2">Your applicants couldn&apos;t be loaded.</p>
                <Button variant="outline-danger" onClick={load}>
                    Retry
                </Button>
            </Alert>
        );
    } else {
        const anyone = state.sections.some(
            ({ result }) =>
                result.status !== 'ok' || result.candidacies.length > 0
        );
        body = anyone ? (
            state.sections.map(({ position, result }) => (
                <SectionBlock
                    key={position.applicationId}
                    position={position}
                    result={result}
                />
            ))
        ) : (
            <p>{EMPTY_TEXT}</p>
        );
    }

    return (
        <Card className="border-0 shadow-sm mt-4">
            <Card.Header className="bg-transparent border-0">
                <h2 className="h5 mb-0">LA hiring</h2>
            </Card.Header>
            <Card.Body>
                {body}
                <Link to="/my-positions">Go to My LA positions</Link>
            </Card.Body>
        </Card>
    );
}
